import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resetDeviceProfiles } from '../../../api/backendAPI';
import { useFavoritesContext } from '../../../contexts/FavoritesContext';
import { useWatchHistory } from '../../../contexts/WatchHistoryContext';
import { useWatchProgress } from '../../../contexts/WatchProgressContext';
import { useDevice } from '../../../contexts/DeviceContext';
import { useLiveHistory } from '../../../contexts/LiveHistoryContext';
import { useWatchSync } from '../../../hooks/useWatchSync';
import { clearApplicationDataPreservingActivation, closeOrRestartApplication } from '../../../utils/appLifecycle';
import { clearCatalogCache, clearPlaybackLocalData } from '../../../utils/cacheUtils';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';

interface CleanupSectionProps {
  focused: boolean;
}

type CleanupKey = 'catalog' | 'playback';

const CleanupSection: React.FC<CleanupSectionProps> = ({ focused }) => {
  const { t } = useTranslation();
  const { deviceId, activationPassword } = useDevice();
  const { clearAll: clearAllRemote } = useWatchSync();
  const { clearAllLocal: clearHistoryLocal } = useWatchHistory();
  const { clearAllLocal: clearProgressLocal } = useWatchProgress();
  const { clearAllLocal: clearFavoritesLocal } = useFavoritesContext();
  const { clearAllLocal: clearLiveHistoryLocal } = useLiveHistory();
  const [cleared, setCleared] = useState<CleanupKey | null>(null);
  const [confirmation, setConfirmation] = useState<'playback' | 'reset' | null>(null);
  const [loading, setLoading] = useState<'playback' | 'reset' | null>(null);
  const [error, setError] = useState('');
  const firstBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (focused) firstBtnRef.current?.focus();
  }, [focused]);

  const flashCleared = (key: CleanupKey) => {
    setCleared(key);
    setTimeout(() => setCleared(null), 2_000);
  };

  const handleCatalog = async () => {
    setError('');
    try {
      await clearCatalogCache();
      flashCleared('catalog');
      setTimeout(() => window.location.reload(), 1_500);
    } catch {
      setError(t('settings.cleanup.catalogError', 'Não foi possível limpar todo o cache do catálogo.'));
    }
  };

  const handlePlayback = async () => {
    setError('');
    setLoading('playback');
    try {
      await clearAllRemote();
      clearHistoryLocal();
      clearProgressLocal();
      clearFavoritesLocal();
      clearLiveHistoryLocal();
      clearPlaybackLocalData();
      flashCleared('playback');
      setTimeout(() => window.location.reload(), 1_500);
    } catch {
      setError(t(
        'settings.cleanup.remoteError',
        'Não foi possível confirmar a limpeza no servidor. Nenhum dado local foi apagado.',
      ));
    } finally {
      setLoading(null);
    }
  };

  const handleReset = async () => {
    setError('');
    if (!deviceId || !activationPassword) {
      setError(t('settings.cleanup.resetDeviceError', 'Dispositivo não identificado. Reabra o aplicativo e tente novamente.'));
      return;
    }
    setLoading('reset');
    try {
      await resetDeviceProfiles(deviceId, activationPassword);
      await clearApplicationDataPreservingActivation();
      closeOrRestartApplication();
    } catch {
      setError(t(
        'settings.cleanup.resetRemoteError',
        'Não foi possível redefinir no servidor. O aplicativo não foi alterado.',
      ));
      setLoading(null);
    }
  };

  return (
    <div className="settings-section">
      <h2 className="settings-section__title">{t('settings.cleanup.title')}</h2>

      <div className="settings-card">
        <div className="settings-cleanup-row">
          <div className="settings-cleanup-row__text">
            <p className="settings-cleanup-row__name">
              {t('settings.cleanup.catalog', 'Limpar cache do catálogo')}
            </p>
            <p className="settings-cleanup-row__desc">
              {t('settings.cleanup.catalogDesc', 'Apaga catálogo, metadados, imagens, busca e EPG sem remover dados de reprodução.')}
            </p>
          </div>
          {cleared === 'catalog' ? (
            <span className="settings-cleared-msg">{t('settings.cleanup.cleared')}</span>
          ) : (
            <button ref={firstBtnRef} className="settings-btn" onClick={() => { void handleCatalog(); }}>
              {t('settings.cleanup.catalogBtn', 'Limpar cache')}
            </button>
          )}
        </div>

        <div className="settings-cleanup-row">
          <div className="settings-cleanup-row__text">
            <p className="settings-cleanup-row__name">
              {t('settings.cleanup.playback', 'Apagar dados de reprodução')}
            </p>
            <p className="settings-cleanup-row__desc">
              {t('settings.cleanup.playbackDesc')}
            </p>
          </div>
          {cleared === 'playback' ? (
            <span className="settings-cleared-msg">{t('settings.cleanup.cleared')}</span>
          ) : (
            <button
              className="settings-btn settings-btn--danger"
              onClick={() => setConfirmation('playback')}
              disabled={loading !== null}
            >
              {loading === 'playback' ? '...' : t('settings.cleanup.playbackBtn')}
            </button>
          )}
        </div>

        <div className="settings-cleanup-row">
          <div className="settings-cleanup-row__text">
            <p className="settings-cleanup-row__name">{t('settings.cleanup.resetApp', 'Redefinir aplicativo')}</p>
            <p className="settings-cleanup-row__desc">
              {t(
                'settings.cleanup.resetDesc',
                'Remove perfis e configurações, preserva a ativação e fecha o aplicativo.',
              )}
            </p>
          </div>
          <button
            className="settings-btn settings-btn--danger"
            onClick={() => setConfirmation('reset')}
            disabled={loading !== null}
          >
            {loading === 'reset' ? '...' : t('settings.cleanup.resetApp', 'Redefinir aplicativo')}
          </button>
        </div>

        {error && <p style={{ margin: 0, color: '#f87171', fontSize: 13 }}>{error}</p>}
      </div>

      {confirmation === 'playback' && (
        <ConfirmationModal
          title={t('settings.cleanup.playback', 'Apagar dados de reprodução')}
          message={t('settings.cleanup.playbackConfirmDesc')}
          confirmLabel={t('settings.cleanup.playbackConfirmBtn')}
          onCancel={() => setConfirmation(null)}
          onConfirm={() => {
            setConfirmation(null);
            void handlePlayback();
          }}
        />
      )}
      {confirmation === 'reset' && (
        <ConfirmationModal
          title={t('settings.cleanup.resetApp', 'Redefinir aplicativo')}
          message={t(
            'settings.cleanup.resetConfirmDesc',
            'Os perfis e dados locais serão removidos, a ativação será preservada e o aplicativo será fechado.',
          )}
          confirmLabel={t('settings.cleanup.resetConfirm')}
          onCancel={() => setConfirmation(null)}
          onConfirm={() => {
            setConfirmation(null);
            void handleReset();
          }}
        />
      )}
    </div>
  );
};

export default CleanupSection;
